import { useState, type ReactElement } from "react";
import {
    SideNavProvider,
    useSideNav,
} from "@design-system/SideNav/SideNavProvider";
import { SideNavShowButton } from "@design-system/SideNav/SideNavShowButton";
import { useAnatomyMode } from "./componentAnatomyMode";
import { BooleanControl, SelectControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import { OrgSideNav, PersonalSideNav } from "./SideNavPlaygroundOrg";
import type { SideNavCompositionProps } from "./SideNavPlaygroundParts";
import { SideNavStateMatrix } from "./SideNavPlaygroundStates";
import { WorkspaceSideNav } from "./SideNavPlaygroundWorkspace";
import controlStyles from "./structureLayoutControls.module.scss";
import styles from "./SideNavPlayground.module.scss";

type Composition = "workspace" | "org" | "personal";
type AnatomyRow = "item" | "activeItem" | "subItem" | "section";

const compositionOptions: ReadonlyArray<{ value: Composition; label: string }> =
    [
        { value: "workspace", label: "Workspace" },
        { value: "org", label: "Org" },
        { value: "personal", label: "Personal" },
    ];

const anatomyRowOptions: ReadonlyArray<{ value: AnatomyRow; label: string }> = [
    { value: "item", label: "Item" },
    { value: "activeItem", label: "Active item" },
    { value: "subItem", label: "Sub-item" },
    { value: "section", label: "Section header" },
];

const compositions: Record<
    Composition,
    (props: SideNavCompositionProps) => ReactElement
> = {
    workspace: WorkspaceSideNav,
    org: OrgSideNav,
    personal: PersonalSideNav,
};

const SideNavSandbox = () => {
    const { mode, setMode, isHidden, setHidden } = useSideNav();
    const [composition, setComposition] = useState<Composition>("workspace");
    const [anatomyRow, setAnatomyRow] = useState<AnatomyRow>("item");
    const anatomy = useAnatomyMode({
        component: "sideNav",
        variantId: anatomyRow,
        isDisabled: false,
    });
    const forcedRowState =
        anatomy.forcedState === "pressed" ? undefined : anatomy.forcedState;
    const CompositionNav = compositions[composition];
    const summary = [composition, mode, isHidden && "hidden"]
        .filter(Boolean)
        .join(" · ");

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <section>
                        <h2>Nav</h2>
                        <div className={controlStyles.fields}>
                            <SelectControl
                                label="Composition"
                                options={compositionOptions}
                                value={composition}
                                onChange={setComposition}
                            />
                            <SelectControl
                                label="X-ray row"
                                options={anatomyRowOptions}
                                value={anatomyRow}
                                onChange={setAnatomyRow}
                            />
                        </div>
                    </section>
                    <section>
                        <h2>Visibility</h2>
                        <div className={controlStyles.booleanGrid}>
                            <BooleanControl
                                label="Hidden"
                                checked={isHidden}
                                onChange={setHidden}
                            />
                            <BooleanControl
                                label="Mini"
                                checked={mode === "mini"}
                                onChange={(isMini) =>
                                    setMode(isMini ? "mini" : "open")
                                }
                            />
                        </div>
                        <p className={controlStyles.hint}>
                            Only clicks hide and show the nav, and it pushes the
                            page aside as it slides. The nav's right edge (or
                            Enter on it) switches open and mini; below 800px it
                            starts mini. Workspace shows the unshipped Skills
                            row; org shows the unshipped Automation section and
                            client task rows.
                        </p>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.isActive ? anatomy.stageWidth : "wide"}
                summary={anatomy.isActive ? `x-ray · ${summary}` : summary}
                belowStage={<SideNavStateMatrix />}
                output={JSON.stringify(
                    { composition, mode, isHidden },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? (
                    <div className={styles.frame}>
                        <CompositionNav
                            key={composition}
                            className={styles.nav}
                            forcedRowState={forcedRowState}
                        />
                        <div className={styles.page}>
                            <div className={styles.pageHeader}>
                                <SideNavShowButton />
                                Transactions
                            </div>
                        </div>
                    </div>
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};

/**
 * @purpose SideNav sandbox: workspace, org and personal compositions in a
 * frame that hides, shows and switches between open and mini, a matrix of
 * every row and section state, and the row x-ray.
 */
export const SideNavPlayground = () => (
    <SideNavProvider storageKey="kick:design-system:side-nav:state">
        <SideNavSandbox />
    </SideNavProvider>
);
